import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, input, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TagModule } from '@openng/optimus-ui/tag';
import { TimelineModule } from '@openng/optimus-ui/timeline';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import cloneDeep from 'lodash-es/cloneDeep';
import orderBy from 'lodash-es/orderBy';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import type { FoundationModelService } from '@app/modules/assets/services/foundation-model.service';
import type { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import type { PromptService } from '@app/modules/assets/services/prompt.service';
import type { RagService } from '@app/modules/assets/services/rag.service';
import { MilkdownComponent } from '@app/shared/components/milkdown/milkdown.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    AUDIT_ACTION,
    type OBJECTS,
    REVIEW_STATUS,
    SYSTEM_APPROVALS,
    WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AttachmentService } from '@app/shared/services/attachment.service';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import { parseAsNumber } from '@app/shared/utils/helpers';

interface ApprovalLog {
    newStatus: WORKFLOW_STATUS | REVIEW_STATUS;
    status: WORKFLOW_STATUS | REVIEW_STATUS;
    createdDate: string;
    action: string;
    actorName: string;
    createdBy: string;
    actorResponsibility: string;
    recipientName: string;
    recipientResponsibility: string;
    responsibilityId: number;
    reviewerNames: string[];
    comment: string;
    actor: string;
}

@Component({
    selector: 'cor-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss'],
    imports: [
        TooltipModule,
        NoDataComponent,
        StatusIconComponent,
        RouterModule,
        LocalDatePipe,
        SkeletonComponent,
        SelectButtonModule,
        FormsModule,
        TimelineModule,
        NameBadgePipe,
        UniqueRgbColorPipe,
        NgStyle,
        NgTemplateOutlet,
        TagModule,
        ButtonModule,
        AvatarModule,
        MilkdownComponent,
    ],
})
export class TimelineComponent implements OnInit {
    readonly object = input.required<OBJECTS>();
    readonly id = input.required({ transform: parseAsNumber });
    readonly service = input.required<
        FoundationModelService | PromptService | RagService | PipelineService
    >();

    protected SYSTEM_APPROVALS = SYSTEM_APPROVALS;
    protected WORKFLOW_STATUS = WORKFLOW_STATUS;
    protected REVIEW_STATUS = REVIEW_STATUS;
    protected AUDIT_ACTION = AUDIT_ACTION;

    protected readonly filterBy = signal('all');
    protected logs: ApprovalLog[] = [];
    protected allLogs: ApprovalLog[] = [];
    protected readonly isLoading = signal(true);
    private readonly currentUser = inject(AuthenticationService).currentUser;

    private readonly attachmentService = inject(AttachmentService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.service()
            .getAudit(this.id())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: ListResponse<any>) => {
                this.logs = response.result.reverse();
                this.logs.forEach(item => {
                    /**
                     * We need to filter by 'actor' to show current logged in user logs.
                     * If we format 'actor', then it is not possible to filter using 'actor'.
                     * So, using a new property 'actorName' to display formatted value and
                     * using 'actor' to filter.
                     */
                    item.actorName = item?.actor ? item.actor : null;
                    // Preparing timeline data.
                    if (
                        item?.actor !== SYSTEM_APPROVALS.WORKFLOW_RULE &&
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                        item?.action !== AUDIT_ACTION.CHANGED
                    ) {
                        item.actorName = item?.actorResponsibility
                            ? `${item?.actorName || ''} (${item?.actorResponsibility})`
                            : `${item?.actorName || ''} (Requestor)`;
                    }

                    if (item?.recipientName !== SYSTEM_APPROVALS.ALL) {
                        item.recipientName += item?.recipientResponsibility
                            ? ` (${item?.recipientResponsibility})`
                            : ' (Requestor)';
                    }
                });

                this.allLogs = cloneDeep(this.logs);
            })
            .add(() => {
                this.isLoading.set(false);
            });
    }

    protected onFilterbyUser() {
        if (this.filterBy() === 'all') {
            this.logs = orderBy(cloneDeep(this.allLogs), dateObj => new Date(dateObj.createdDate), [
                'desc',
            ]);
        } else {
            const list = this.allLogs.filter(x => x.actor === this.currentUser()?.name);
            this.logs = orderBy(list, dateObj => new Date(dateObj.createdDate), ['desc']);
        }
    }

    protected downloadFile(item: { id: number }) {
        this.attachmentService.saveAs(item.id);
    }
}
